//-- Copyright (c) 2021 TMT Insights Group, LLC
import { LabelValue, MetaGroup } from '@/components/_common';
import CollapseList from '@/components/_common/CollapseList';
import { selectAudioTracks, selectJobMetadata, selectSubtitleTracks } from '@/redux/selectors/jobSelectors';
import styled from '@emotion/styled';
import { Fade, Grid, Grow, Palette, Typography, useTheme } from '@mui/material';
import JobDeliverables from '../JobTabAssetStatus/JobDeliverables';
import { selectAudioLanguages, selectBaseLanguages, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import SimpleLabelValue from '@/components/_common/SimpleLabelValue';
import { useAppSelector } from '@/redux/hooks';

const DeliverablesV1 = () => {
  const metadata = useAppSelector(selectJobMetadata);
  const audioTracks = useAppSelector(selectAudioTracks);
  const subTracks = useAppSelector(selectSubtitleTracks);
  const Languages = useAppSelector(selectBaseLanguages);
  const AudioLanguages = useAppSelector(selectAudioLanguages);
  const SubtitlesLanguages = useAppSelector(selectSubtitlesLanguages);
  const theme = useTheme();

  enum DeliverableColors {
    MEDIA = 'primary',
    AUDIO = 'secondary',
    SUBTITLE = 'default'
  }

  const renderAudioTrack = (track: any, i: number) => {
    return (
      <Fade key={`audiotrack_${track.track}_${i}`} in timeout={(i + 1) * 300}>
        <Track>
          <div>
            <TrackNum color={DeliverableColors.AUDIO}>{track.track}</TrackNum>
          </div>
          <div>
            <Typography noWrap>{track.type || '-'}</Typography>
          </div>
          <div>
            <Typography noWrap>{metadata['audio-track-encoding'] || '-'}</Typography>
          </div>
          <div>{getLanguageLabel(track.lang, 'audio')}</div>
        </Track>
      </Fade>
    );
  };

  const renderSubTrack = (track: any, i: number) => {
    return (
      <Fade key={`subtrack_${track.track}_${i}`} in timeout={(i + 1) * 300}>
        <Track>
          <div>
            <TrackNum color={DeliverableColors.SUBTITLE}>{track.track}</TrackNum>
          </div>
          <div>{track.type || '-'}</div>
          <div>{getLanguageLabel(track.lang, 'subtitle')}</div>
        </Track>
      </Fade>
    );
  };

  const getLanguageLabel = (languageCode: string, type: 'audio' | 'subtitle') => {
    const baseLanguages = type === 'audio' ? AudioLanguages : SubtitlesLanguages;
    const langCodes = [languageCode, languageCode.replace('-', '_')];
    const lang = baseLanguages?.find((l) => langCodes.includes(l.code)) || Languages?.find((l) => langCodes.includes(l.code));
    return SimpleLabelValue({ label: lang?.label, value: lang?.code, fallback: '-' });
  };

  return (
    <>
      <JobDeliverables />
      <Grow in timeout={600}>
        <div>
          <MetaGroup label="Media" color={DeliverableColors.MEDIA}>
            <Grid container>
              <StyledLabelValue label="Resolution" size={{ xs: 6 }}>
                {metadata?.resolution}
              </StyledLabelValue>
              <StyledLabelValue label="Bitrate" size={{ xs: 6 }}>
                {metadata?.['media-bit-rate']}
              </StyledLabelValue>
              <LabelValue label="Aspect Ratio" size={{ xs: 6 }}>
                {metadata?.['media-aspect-ratio']}
              </LabelValue>
              <LabelValue label="Encoding" size={{ xs: 6 }}>
                {metadata?.['media-encoding']}
              </LabelValue>
            </Grid>
          </MetaGroup>
        </div>
      </Grow>
      {!!audioTracks.length && (
        <MetaGroup label="Audio" color={DeliverableColors.AUDIO}>
          <CollapseList
            visibleItems={
              <Tracks>
                <span>Track #</span>
                <span>Type</span>
                <span>Encoding</span>
                <span>Language</span>
                {audioTracks.slice(0, 3).map((audioTrack: any, i: number) => renderAudioTrack(audioTrack, i))}
              </Tracks>
            }
            collapsedItems={audioTracks.slice(3).map((audioTrack: any, i: number) => renderAudioTrack(audioTrack, i))}
            color={theme.palette[DeliverableColors.AUDIO].main}
          />
        </MetaGroup>
      )}

      {!!subTracks.length && (
        <MetaGroup label="Subtitle" color={DeliverableColors.SUBTITLE}>
          <CollapseList
            visibleItems={
              <Tracks>
                <span>Track #</span>
                <span>Type</span>
                <span>Language</span>
                {subTracks.slice(0, 3).map((subTrack: any, i: number) => renderSubTrack(subTrack, i))}
              </Tracks>
            }
            collapsedItems={subTracks.slice(3).map((subTrack: any, i: number) => renderSubTrack(subTrack, i))}
            color={theme.palette[DeliverableColors.SUBTITLE].main}
          />
        </MetaGroup>
      )}
    </>
  );
};

export default DeliverablesV1;

const StyledLabelValue = styled(LabelValue)`
  margin-bottom: 20px;
`;

const Tracks = styled.div`
  & > span {
    white-space: nowrap;
    display: inline-block;
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
    opacity: 0.7;
    padding-right: ${({ theme }) => theme.spacing(2)};
    font-size: 0.85rem;
    width: 25%;

    &:first-of-type {
      min-width: 60px;
      max-width: 60px;
    }

    &:last-of-type {
      padding-right: 0px;
    }
  }
`;

const Track = styled.div`
  padding: ${({ theme }) => theme.spacing(0.5, 0)};
  display: flex;
  align-items: center;
  white-space: nowrap;

  & > div {
    display: inline-block;
    padding-right: ${({ theme }) => theme.spacing(2)};
    overflow: hidden;
    min-width: 25%;

    &:first-of-type {
      min-width: 60px;
      max-width: 60px;
    }
  }
`;

const TrackNum = styled.mark<{ color: keyof Palette }>`
  line-height: 1rem;
  height: 20px;
  width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0px;
  border-radius: 5px;
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  border-width: 1px;
  border-style: solid;

  border: ${({ theme }) => theme.palette.secondary.main + '8a'};
  background: ${({ theme, color }) => theme.palette[color].main + '8a'};
`;
